<template>
  <div>
    <!-- ── Top bar: entrance + live status ── -->
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div class="mb-2 mr-3">
        <div class="text-secondary small">{{ currentDate }}</div>
        <div class="d-flex align-items-center small text-secondary">
          <span class="d-inline-block rounded-circle mr-1" :class="refreshing ? 'bg-secondary' : 'bg-primary'" style="width:8px;height:8px;"></span>
          Live · updated {{ updatedAgo }}
          <button class="btn btn-link btn-sm text-secondary p-0 ml-2" :disabled="refreshing" title="Refresh" @click="load(true)">
            <i class="bi bi-arrow-clockwise"></i>
          </button>
        </div>
      </div>

      <div v-if="qrCodes.length > 1 && qrCodes.length <= 4" class="btn-group mb-2 flex-wrap" role="group">
        <button
          v-for="qr in qrCodes"
          :key="qr.id"
          type="button"
          class="btn"
          :class="selectedQrId === qr.id ? 'btn-dark' : 'btn-outline-secondary'"
          @click="selectQr(qr.id)"
        >
          {{ qr.label || qr.package }}
          <span class="badge badge-light ml-1">{{ qr.inside_now }}</span>
        </button>
      </div>
      <b-form-select
        v-else-if="qrCodes.length > 4"
        v-model="selectedQrId"
        :options="qrOptions"
        class="w-auto mb-2"
        @change="load()"
      />
    </div>

    <!-- No entrances -->
    <div v-if="!loadingQr && !qrCodes.length" class="text-center py-5">
      <i class="bi bi-qr-code text-secondary" style="font-size:2.5rem;"></i>
      <h6 class="font-weight-bold text-dark mt-2">No entrances yet</h6>
      <p class="text-secondary mb-0">Create a check-in QR code first (Subscriptions › More › QRCode Manager).</p>
    </div>

    <div v-else-if="!selectedQrId" class="text-center text-secondary py-5">
      <i class="bi bi-door-open d-block mb-2" style="font-size:2rem;"></i>
      Choose an entrance to start.
    </div>

    <template v-else>
      <!-- ── KPIs ── -->
      <div class="row mb-3" style="row-gap:14px;">
        <div v-for="k in kpis" :key="k.label" class="col-6 col-md-3">
          <div class="text-secondary small mb-1">{{ k.label }}</div>
          <div class="d-flex align-items-center" style="gap:8px;">
            <span class="h4 font-weight-bold mb-0" :class="k.cls || 'text-dark'">{{ k.value }}</span>
            <span v-if="k.badge" class="badge badge-pill badge-light text-secondary">{{ k.badge }}</span>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- ── LEFT: front desk actions ── -->
        <div class="col-12 col-lg-5 mb-3">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white pb-0 border-0">
              <ul class="nav nav-pills nav-fill">
                <li v-for="t in tabs" :key="t.key" class="nav-item">
                  <a class="nav-link py-2" :class="{ active: tab === t.key }" href="#" @click.prevent="tab = t.key">
                    <i :class="t.icon" class="mr-1"></i>{{ t.label }}
                  </a>
                </li>
              </ul>
            </div>
            <div class="card-body">
              <!-- Scan pass -->
              <div v-show="tab === 'scan'">
                <p class="text-secondary small mb-3">Scan the member's app pass with the camera or a barcode scanner.</p>
                <scan-pass-box ref="scanner" embedded :qr-code-id="selectedQrId" @checked-in="load(true)" />
              </div>

              <!-- Entry code -->
              <div v-show="tab === 'code'">
                <p class="text-secondary small mb-3">Member shows the 6-digit code from their SMS.</p>
                <input
                  v-model="entryOtp"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  class="form-control form-control-lg text-center font-weight-bold mb-2"
                  placeholder="000000"
                  @keyup.enter="confirmEntry"
                />
                <div class="d-flex align-items-center mb-3">
                  <span class="text-secondary small mr-2">People entering</span>
                  <div class="btn-group">
                    <button class="btn btn-outline-secondary" :disabled="entryCount <= 1" @click="entryCount--">−</button>
                    <span class="btn btn-light disabled px-3">{{ entryCount }}</span>
                    <button class="btn btn-outline-secondary" @click="entryCount++">+</button>
                  </div>
                </div>
                <button class="btn btn-primary btn-lg btn-block" :disabled="busy || entryOtp.length < 4" @click="confirmEntry">
                  <span v-if="busy" class="spinner-border spinner-border-sm mr-2"></span>Confirm entry
                </button>
              </div>

              <!-- Check out -->
              <div v-show="tab === 'exit'">
                <p class="text-secondary small mb-3">Find who's leaving, or use <i class="bi bi-box-arrow-right"></i> in the list.</p>
                <div class="btn-group w-100 mb-2">
                  <button class="btn" :class="exitLookupType === 'phone' ? 'btn-dark' : 'btn-outline-secondary'" @click="exitLookupType = 'phone'; exitLookup = ''">Phone</button>
                  <button class="btn" :class="exitLookupType === 'otp' ? 'btn-dark' : 'btn-outline-secondary'" @click="exitLookupType = 'otp'; exitLookup = ''">Entry code</button>
                </div>
                <input
                  v-model="exitLookup"
                  type="text"
                  class="form-control form-control-lg mb-3"
                  :placeholder="exitLookupType === 'phone' ? 'e.g. 0712345678' : '6-digit code'"
                  @keyup.enter="recordExit"
                />
                <button class="btn btn-dark btn-lg btn-block" :disabled="busy || !exitLookup" @click="recordExit">
                  <span v-if="busy" class="spinner-border spinner-border-sm mr-2"></span>Check out
                </button>
              </div>

              <!-- Result (code / exit) -->
              <div v-if="result && tab !== 'scan'" class="rounded p-3 mt-3 text-white" :class="result.ok ? 'bg-primary' : 'bg-danger'">
                <div class="d-flex align-items-center">
                  <i :class="result.ok ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'" style="font-size:1.75rem;"></i>
                  <div class="ml-3">
                    <div class="font-weight-bold">{{ result.title }}</div>
                    <div class="small">{{ result.detail }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── RIGHT: who's inside + today ── -->
        <div class="col-12 col-lg-7 mb-3">
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-white d-flex flex-wrap justify-content-between align-items-center">
              <div class="font-weight-bold text-dark mr-2">
                Inside now <span class="badge badge-pill badge-primary ml-1">{{ insideNow.length }}</span>
              </div>
              <div class="input-group w-auto mt-2 mt-md-0">
                <div class="input-group-prepend"><span class="input-group-text bg-white"><i class="bi bi-search"></i></span></div>
                <input v-model="search" type="text" class="form-control" placeholder="Search name or phone" />
              </div>
            </div>

            <div v-if="loadingDashboard" class="text-center py-4"><b-spinner small /></div>
            <div v-else-if="!filteredInside.length" class="text-center text-secondary py-4">
              {{ search ? 'No match.' : 'Nobody inside right now.' }}
            </div>
            <ul v-else class="list-group list-group-flush" style="max-height:340px;overflow-y:auto;">
              <li v-for="m in filteredInside" :key="m.attendance_id" class="list-group-item d-flex align-items-center">
                <span
                  class="rounded-circle bg-dark text-white d-inline-flex align-items-center justify-content-center font-weight-bold mr-3 flex-shrink-0"
                  style="width:38px;height:38px;"
                >{{ initials(m.customer_name) }}</span>
                <div class="flex-grow-1 mr-2" style="min-width:0;">
                  <div class="font-weight-bold text-dark text-truncate">{{ m.customer_name }}</div>
                  <small class="text-secondary">
                    In {{ m.entered_at }} · {{ since(m.entered_at) }}
                    <span v-if="m.check_in_type !== 'subscription'" class="badge badge-secondary ml-1">{{ typeLabel(m.check_in_type) }}</span>
                  </small>
                </div>
                <button class="btn btn-outline-dark btn-sm flex-shrink-0" :disabled="checkingOut === m.attendance_id" @click="checkout(m)">
                  <span v-if="checkingOut === m.attendance_id" class="spinner-border spinner-border-sm"></span>
                  <template v-else><i class="bi bi-box-arrow-right mr-1"></i>Out</template>
                </button>
              </li>
            </ul>
          </div>

          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white d-flex justify-content-between align-items-center">
              <span class="font-weight-bold text-dark">Today</span>
              <small class="text-secondary">{{ todayLog.length }} visit{{ todayLog.length === 1 ? '' : 's' }}</small>
            </div>
            <div v-if="!filteredLog.length" class="text-center text-secondary py-4">No visits yet today.</div>
            <ul v-else class="list-group list-group-flush" style="max-height:300px;overflow-y:auto;">
              <li v-for="row in filteredLog" :key="row.attendance_id" class="list-group-item d-flex justify-content-between align-items-center">
                <div class="mr-2" style="min-width:0;">
                  <div class="text-dark text-truncate">{{ row.customer_name }}</div>
                  <small class="text-secondary">{{ row.customer_phone }}</small>
                </div>
                <div class="text-right flex-shrink-0">
                  <div class="small text-dark">{{ row.entered_at }} → {{ row.exited_at || '…' }}</div>
                  <span v-if="!row.exited_at" class="badge badge-primary">Inside</span>
                  <span v-else-if="row.check_in_type !== 'subscription'" class="badge badge-secondary">{{ typeLabel(row.check_in_type) }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import ScanPassBox from './ScanPassBox.vue'

/**
 * Front-desk attendance: scan passes, confirm SMS entry codes, check people
 * out, and see who's inside. Used by the Attendance Dashboard modal and the
 * /subscription-checkin page.
 */
export default {
  name: 'AttendanceDesk',

  components: { ScanPassBox },

  props: {
    // Stops auto-refresh while the hosting modal is closed.
    active: { type: Boolean, default: true },
  },

  data() {
    return {
      qrCodes:          [],
      selectedQrId:     '',
      loadingQr:        true,
      loadingDashboard: false,
      refreshing:       false,
      updatedAt:        null,
      now:              Date.now(),

      summary:   { inside_now: 0, today_entries: 0, today_exits: 0, subscription: 0, wallet: 0 },
      insideNow: [],
      todayLog:  [],
      search:    '',

      tab:            'scan',
      busy:           false,
      result:         null,
      entryOtp:       '',
      entryCount:     1,
      exitLookupType: 'phone',
      exitLookup:     '',
      checkingOut:    null,

      refreshTimer: null,
      clockTimer:   null,
      resultTimer:  null,
    }
  },

  computed: {
    tabs() {
      return [
        { key: 'scan', label: 'Scan pass',  icon: 'bi bi-qr-code-scan' },
        { key: 'code', label: 'Entry code', icon: 'bi bi-123' },
        { key: 'exit', label: 'Check out',  icon: 'bi bi-box-arrow-right' },
      ]
    },
    qrOptions() {
      return [{ value: '', text: '— Select entrance —' }, ...this.qrCodes.map(q => ({ value: q.id, text: `${q.label || q.package} (${q.inside_now} inside)` }))]
    },
    kpis() {
      const s = this.summary
      return [
        { label: 'Inside now',    value: this.insideNow.length, cls: 'text-primary' },
        { label: 'Entries today', value: s.today_entries || 0 },
        { label: 'Exits today',   value: s.today_exits || 0 },
        { label: 'Members / walk-ins', value: `${s.subscription || 0} / ${(s.wallet || 0) + (s.manual_walkin || 0)}` },
      ]
    },
    currentDate() {
      return moment().format('dddd, D MMMM YYYY')
    },
    updatedAgo() {
      if (!this.updatedAt) return '—'
      const secs = Math.max(0, Math.round((this.now - this.updatedAt) / 1000))
      return secs < 5 ? 'just now' : secs < 60 ? `${secs}s ago` : `${Math.round(secs / 60)}m ago`
    },
    filteredInside() { return this.filter(this.insideNow) },
    filteredLog()    { return this.filter(this.todayLog) },
  },

  watch: {
    active(val) {
      if (val) this.load(true)
      else if (this.$refs.scanner) this.$refs.scanner.stopCamera()
    },
    tab(val) {
      if (val !== 'scan' && this.$refs.scanner) this.$refs.scanner.stopCamera()
    },
  },

  mounted() {
    this.loadQrCodes()
    this.refreshTimer = setInterval(() => { if (this.active && this.selectedQrId) this.load(true) }, 30000)
    this.clockTimer   = setInterval(() => { this.now = Date.now() }, 5000)
  },

  beforeDestroy() {
    clearInterval(this.refreshTimer)
    clearInterval(this.clockTimer)
    clearTimeout(this.resultTimer)
  },

  methods: {
    async loadQrCodes() {
      this.loadingQr = true
      try {
        const { data } = await axios.get('/api/subscriptions/checkin/qr-codes')
        this.qrCodes = data
        let remembered = ''
        try { remembered = localStorage.getItem('dk_desk_entrance') || '' } catch (e) { /* ignore */ }
        const pick = data.find(q => q.id === remembered) || (data.length === 1 ? data[0] : null)
        if (pick) this.selectQr(pick.id)
      } catch (e) {
        this.toast('Could not load entrances.', 'danger')
      } finally {
        this.loadingQr = false
      }
    },

    selectQr(id) {
      this.selectedQrId = id
      try { localStorage.setItem('dk_desk_entrance', id) } catch (e) { /* ignore */ }
      this.load()
    },

    async load(silent = false) {
      if (!this.selectedQrId) return
      silent ? (this.refreshing = true) : (this.loadingDashboard = true)
      try {
        const { data } = await axios.get('/api/subscriptions/checkin/attendant/dashboard', {
          params: { qr_code_id: this.selectedQrId },
          _silent: true,
        })
        this.summary   = data.summary
        this.insideNow = data.inside_now
        this.todayLog  = data.today_log
        this.updatedAt = Date.now()
        this.now       = Date.now()
        const qr = this.qrCodes.find(q => q.id === this.selectedQrId)
        if (qr) qr.inside_now = data.inside_now.length
      } catch (e) {
        if (!silent) this.toast('Could not load attendance.', 'danger')
      } finally {
        this.loadingDashboard = false
        this.refreshing       = false
      }
    },

    async confirmEntry() {
      if (!this.entryOtp.trim()) return
      this.busy = true
      try {
        const { data } = await axios.post('/api/subscriptions/checkin/attendant/confirm-entry', {
          qr_code_id: this.selectedQrId,
          otp:        this.entryOtp.trim(),
          count:      this.entryCount,
        })
        this.showResult(true, data.customer_name || 'Checked in',
          `Entry #${data.entry_number || 1} · ${data.entered_at || ''}` + (data.amount_debited ? ` · KES ${this.fmt(data.amount_debited)} charged` : ''))
        this.entryOtp   = ''
        this.entryCount = 1
        this.load(true)
      } catch (e) {
        this.showResult(false, 'Not checked in', e.response?.data?.message || 'Entry failed.')
      } finally {
        this.busy = false
      }
    },

    async recordExit() {
      if (!this.exitLookup.trim()) return
      this.busy = true
      try {
        const { data } = await axios.post('/api/subscriptions/checkin/attendant/record-exit', {
          qr_code_id:  this.selectedQrId,
          lookup:      this.exitLookup.trim(),
          lookup_type: this.exitLookupType,
        })
        this.showResult(true, `${data.customer_name || 'Member'} checked out`, `In ${data.entered_at} · Out ${data.exited_at}`)
        this.exitLookup = ''
        this.load(true)
      } catch (e) {
        this.showResult(false, 'Not checked out', e.response?.data?.message || 'Exit failed.')
      } finally {
        this.busy = false
      }
    },

    async checkout(m) {
      this.checkingOut = m.attendance_id
      try {
        await axios.post(`/api/subscriptions/checkin/attendant/checkout/${m.attendance_id}`)
        this.toast(`${m.customer_name} checked out`, 'primary')
        this.load(true)
      } catch (e) {
        this.toast(e.response?.data?.message || 'Check-out failed.', 'danger')
      } finally {
        this.checkingOut = null
      }
    },

    showResult(ok, title, detail) {
      this.result = { ok, title, detail }
      clearTimeout(this.resultTimer)
      this.resultTimer = setTimeout(() => { this.result = null }, 8000)
    },

    filter(rows) {
      const q = this.search.trim().toLowerCase()
      if (!q) return rows
      return rows.filter(r => (r.customer_name || '').toLowerCase().includes(q) || (r.customer_phone || '').includes(q))
    },

    since(hhmm) {
      const t = moment(hhmm, 'HH:mm')
      const mins = Math.max(0, moment(this.now).diff(t, 'minutes'))
      return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`
    },

    initials(name) {
      return (name || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('')
    },

    typeLabel(t) {
      return { wallet: 'Wallet', manual_walkin: 'Walk-in', subscription: 'Member' }[t] || t
    },

    fmt(v) { return parseFloat(v || 0).toLocaleString('en-KE', { maximumFractionDigits: 0 }) },

    toast(msg, variant) {
      this.$bvToast.toast(msg, { variant, solid: true, autoHideDelay: 2500, toaster: 'b-toaster-bottom-right' })
    },
  },
}
</script>
